import { useChecklistSearch } from '../checklists/useChecklistSearch';
/**
 * Storage entity detail page — a Shelf, Box, or Storage_Batch's own page,
 * mirroring the catalog card detail page. Loads `GET /storage/{type}/{id}`
 * (own record + resolved location + occupancy) in one round-trip, and reuses
 * the existing per-entity reads for the deeper sections: the QR label
 * (`labels`), a contents preview (`contents`) with a link into the Browse
 * drill-down, and recent transactions (`transactions`).
 *
 * Route: `/storage/:type/:id` where `:type` is `shelves|boxes|batches`. The
 * entity id in the URL is the bare ULID body (colon-free path segment), same as
 * every other `/storage/{type}/{id}/…` route.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { LabelTile } from './LabelTile';
import { browseReturnContextFrom, withoutBrowseReturnFlash } from './batch-browse-state';
import { printSingleLabel } from './label-print';
import { CopyIdButton } from '../shared/CopyIdButton';
import { FilterableSelect } from '../shared/FilterableSelect';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';
import { shortDateTimeWithYear } from '../shared/datetime';
import {
  drillChildMatches,
  ENTITY_TYPE_LABEL,
  entityCreatedAt,
  entityDetailHref,
  formatListingDate,
  toDrillChild,
  type DrillChild,
  type SortDirection,
} from './container-listing';
import { useCatalogApi } from '../catalog/catalog-api';
import {
  batchChecklistConfirmationLabel,
  batchChecklistSummaryLabel,
} from '../batches/batch-checklist-onboarding';
import { batchReviewHref } from '../batches/review-navigation';
import { BatchTransferTools } from '../batches/BatchTransferTools';
import {
  OwnershipLabel,
  OwnershipSummary,
  useOwnershipLabels,
} from '../consignment/OwnershipLabel';
import type {
  ReviewChecklistProduct,
  RunChecklistResolutionSummary,
  StorageBatchWorkflowStatus,
} from '../shared/types';
import {
  useStorageApi,
  type ContainerType,
  type LabelResponse,
  type StorageEntityDetailResponse,
  type TransactionsResponse,
} from './storage-api';
import { positionSideHoverPreview, type SideHoverPreviewPosition } from './batch-hover-preview';

const CONTENTS_CARD_PREVIEW_IMAGE_WIDTH = 176;
const CONTENTS_CARD_PREVIEW_IMAGE_HEIGHT = 246;
const CONTENTS_CARD_PREVIEW_GAP = 8;
const CONTENTS_CARD_PREVIEW_PADDING = 10;

function contentsCardPreviewSize(imageCount: number): { width: number; height: number } {
  const visibleImages = Math.max(1, Math.min(imageCount, 2));
  return {
    width:
      visibleImages * CONTENTS_CARD_PREVIEW_IMAGE_WIDTH +
      (visibleImages - 1) * CONTENTS_CARD_PREVIEW_GAP +
      CONTENTS_CARD_PREVIEW_PADDING * 2 +
      2,
    height: CONTENTS_CARD_PREVIEW_IMAGE_HEIGHT + CONTENTS_CARD_PREVIEW_PADDING * 2 + 2,
  };
}

const TYPE_LABEL: Record<ContainerType, string> = {
  racks: 'Rack',
  shelves: 'Shelf',
  boxes: 'Box',
  rows: 'Row',
  batches: 'Storage_Batch',
};

function isContainerType(v: string | undefined): v is ContainerType {
  return v === 'racks' || v === 'shelves' || v === 'boxes' || v === 'rows' || v === 'batches';
}

/** Bare ULID body of an entityId (strip the `tcvs:<type>:` prefix), for URLs. */
function idBody(entityId: string): string {
  const last = entityId.lastIndexOf(':');
  return last === -1 ? entityId : entityId.slice(last + 1);
}

function ReviewStatusBadge({
  status,
}: {
  status: StorageBatchWorkflowStatus | undefined;
}): JSX.Element {
  if (status === undefined) return <span className="tcvs-muted">Unknown</span>;
  if (status.cardCount === 0) return <span className="tcvs-muted">No cards</span>;
  if (status.review !== undefined) {
    if (status.reviewComplete) {
      return (
        <strong className="tcvs-tone-stored">
          Complete {status.review.completedCount}/{status.cardCount}
        </strong>
      );
    }
    return (
      <span>
        <strong className="tcvs-tone-unplaced">In progress</strong>{' '}
        <span className="tcvs-muted">
          {status.review.completedCount}/{status.cardCount} completed
          {(status.review.awaitingImageApprovalCount ?? 0) > 0
            ? `, ${status.review.awaitingImageApprovalCount} awaiting image approval`
            : ''}
          {status.review.imageIssueCount > 0
            ? `, ${status.review.imageIssueCount} image issues`
            : ''}
        </span>
      </span>
    );
  }
  const { approvedCount, pendingCount, rejectedCount } = status.imageApproval;
  if (pendingCount > 0) {
    return (
      <span>
        <strong className="tcvs-tone-unplaced">Pending</strong>{' '}
        <span className="tcvs-muted">
          {approvedCount}/{status.cardCount} approved
          {rejectedCount > 0 ? `, ${rejectedCount} rejected` : ''}
        </span>
      </span>
    );
  }
  if (rejectedCount > 0) {
    return (
      <span>
        <strong className="tcvs-tone-pulled">Reviewed</strong>{' '}
        <span className="tcvs-muted">
          {approvedCount} approved, {rejectedCount} rejected
        </span>
      </span>
    );
  }
  return (
    <span>
      <strong className="tcvs-tone-stored">Approved</strong>{' '}
      <span className="tcvs-muted">
        {approvedCount}/{status.cardCount}
      </span>
    </span>
  );
}

function PutawayStatusBadge({
  status,
}: {
  status: StorageBatchWorkflowStatus | undefined;
}): JSX.Element {
  if (status === undefined) return <span className="tcvs-muted">Unknown</span>;
  return status.putAway ? (
    <strong className="tcvs-tone-stored">Put away</strong>
  ) : (
    <strong className="tcvs-tone-unplaced">Not put away</strong>
  );
}

function checklistLabel(checklist: ReviewChecklistProduct): string {
  const { year, brand, product } = checklist.product;
  return `${year} ${brand} ${product} · ${checklist.category}`;
}

function BatchChecklistControl({
  batchId,
  onApplied,
}: {
  batchId: string;
  onApplied: () => void;
}): JSX.Element {
  const catalog = useCatalogApi();
  const [checklistQuery, setChecklistQuery] = useState('');
  const checklistSearch = useChecklistSearch(catalog.reviewChecklists, checklistQuery);
  const { items: checklists, setItems: setChecklists, loading: checklistLoading } = checklistSearch;
  const [selectedId, setSelectedId] = useState('');
  const [summary, setSummary] = useState<RunChecklistResolutionSummary | null>(null);
  const [previewSummary, setPreviewSummary] = useState<RunChecklistResolutionSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedId === '' || checklists.some((item) => item.checklistId === selectedId)) return;
    let live = true;
    void catalog.reviewChecklists({ checklistId: selectedId }).then((result) => {
      if (!live || !result.ok) return;
      setChecklists((current) => [
        ...new Map(
          [...current, ...result.data.items].map((item) => [item.checklistId, item]),
        ).values(),
      ]);
    });
    return () => {
      live = false;
    };
  }, [catalog, checklists, selectedId]);

  useEffect(() => {
    let live = true;
    setSummary(null);
    setPreviewSummary(null);
    setError(null);
    void (async () => {
      const result = await catalog.getBatchChecklist(batchId);
      if (!live || !result.ok) return;
      const existing = result.data.context?.checklistId ?? '';
      setSelectedId(existing);
    })();
    return () => {
      live = false;
    };
  }, [batchId, catalog]);

  const apply = async () => {
    if (selectedId === '' || busy) return;
    if (previewSummary === null) {
      setBusy(true);
      setError(null);
      setSummary(null);
      const result = await catalog.applyBatchChecklist(batchId, selectedId, true);
      setBusy(false);
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      setPreviewSummary(result.data.summary ?? null);
      return;
    }
    if (!window.confirm(batchChecklistConfirmationLabel(previewSummary))) return;
    setBusy(true);
    setError(null);
    const result = await catalog.applyBatchChecklist(batchId, selectedId);
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setSummary(result.data.summary ?? null);
    setPreviewSummary(null);
    onApplied();
  };

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Checklist</h3>
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label>
            <span className="tcvs-muted" style={{ marginRight: 6 }}>
              Checklist
            </span>
            <FilterableSelect
              value={selectedId}
              disabled={busy}
              ariaLabel="Checklist for this Storage_Batch"
              placeholder="Search checklists"
              loading={checklistLoading}
              searchError={checklistSearch.error}
              hasMore={checklistSearch.hasMore}
              onLoadMore={checklistSearch.loadMore}
              onRetry={checklistSearch.retry}
              onQueryChange={setChecklistQuery}
              options={checklists.map((checklist) => ({
                value: checklist.checklistId,
                label: checklistLabel(checklist),
                searchText: checklist.source?.targetId,
              }))}
              onChange={(value) => {
                setSelectedId(value);
                setPreviewSummary(null);
                setSummary(null);
              }}
            />
          </label>
          <button
            type="button"
            className="tcvs-btn-compact"
            disabled={selectedId === '' || busy}
            onClick={() => void apply()}
          >
            {busy
              ? previewSummary === null
                ? 'Previewing…'
                : 'Applying…'
              : previewSummary === null
                ? 'Preview onboarding'
                : 'Confirm and apply'}
          </button>
        </div>
        {summary !== null ? (
          <p className="tcvs-muted" style={{ margin: '8px 0 0' }}>
            {batchChecklistSummaryLabel(summary)}
          </p>
        ) : null}
        {previewSummary !== null ? (
          <p className="tcvs-muted" style={{ margin: '8px 0 0' }}>
            Preview: {batchChecklistSummaryLabel(previewSummary, true)}. No changes made yet.
          </p>
        ) : null}
        {error !== null ? (
          <p className="tcvs-error" style={{ marginBottom: 0 }}>
            {error}
          </p>
        ) : null}
      </>
    </section>
  );
}

export function StorageEntityDetailPage(): JSX.Element {
  const { type, id } = useParams<{ type: string; id: string }>();
  const api = useStorageApi();
  const { claims } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const browseReturnContext = withoutBrowseReturnFlash(
    browseReturnContextFrom(routerLocation.state),
  );
  const [detail, setDetail] = useState<StorageEntityDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [contentsQuery, setContentsQuery] = useState('');

  // The entity's QR label, fetched once and shown at the top-right of the page
  // (no separate Label section, no View-label button). Best-effort: a failure
  // simply leaves the tile absent rather than blocking the page.
  const [label, setLabel] = useState<LabelResponse | null>(null);

  const refresh = useCallback(async () => {
    if (!isContainerType(type) || id === undefined) {
      setError('invalid_container_type');
      return;
    }
    const res = await api.entityDetail(type, id);
    if (!res.ok) {
      setError(res.message ?? res.error);
      return;
    }
    setError(null);
    setDetail(res.data);
  }, [api, type, id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setContentsQuery('');
  }, [type, id]);

  // Fetch the entity's QR label for the top-right tile (best-effort).
  useEffect(() => {
    if (!isContainerType(type) || id === undefined) return;
    let live = true;
    void api.labels(type, id).then((res) => {
      if (live && res.ok) setLabel(res.data);
    });
    return () => {
      live = false;
    };
  }, [api, type, id]);

  if (!isContainerType(type)) return <p className="tcvs-error">Error: invalid container type</p>;
  if (error !== null) return <p className="tcvs-error">Error: {error}</p>;
  if (detail === null) return <p>Loading…</p>;

  const { entity, location, occupancy, deletion } = detail;
  const isAdmin = claims !== null && hasAnyGroup(claims, ['admin']);
  const typeLabel = TYPE_LABEL[type];
  const backSection = sectionForType(type);
  const batchReturnState = backSection === 'Batches' ? browseReturnContext : undefined;
  const heading =
    entity.entityType === 'RACK' && 'rackCode' in entity
      ? `Rack ${entity.rackCode}`
      : entity.entityType === 'SHELF' && 'shelfCode' in entity
        ? `Shelf ${entity.shelfCode}`
        : entity.entityType === 'ROW' && 'rowCode' in entity && entity.rowCode !== undefined
          ? `Row ${entity.rowCode}`
          : `${typeLabel}`;
  const canDeleteEmptyContainer =
    (entity.entityType === 'RACK' ||
      entity.entityType === 'SHELF' ||
      entity.entityType === 'BOX' ||
      entity.entityType === 'ROW') &&
    deletion.allowed;
  const deleteLabel =
    entity.entityType === 'RACK'
      ? 'Delete rack'
      : entity.entityType === 'SHELF'
        ? 'Delete shelf'
        : entity.entityType === 'BOX'
          ? 'Delete box'
          : entity.entityType === 'ROW'
            ? 'Delete row'
            : 'Delete batch';
  const onDeleteContainer = async () => {
    if (deleting) return;
    if (entity.entityType === 'BATCH' && !isAdmin) return;
    if (entity.entityType !== 'BATCH' && !canDeleteEmptyContainer) return;
    const countText =
      entity.entityType === 'BATCH' && occupancy.tracked
        ? ` and all ${occupancy.childCount} tracked card${occupancy.childCount === 1 ? '' : 's'} inside it`
        : entity.entityType === 'BATCH'
          ? ' and all cards currently stored inside it'
          : '';
    const entityName =
      entity.entityType === 'RACK'
        ? 'Rack'
        : entity.entityType === 'SHELF'
          ? 'Shelf'
          : entity.entityType === 'BOX'
            ? 'Box'
            : entity.entityType === 'ROW'
              ? 'Row'
              : 'Storage_Batch';
    const detail =
      entity.entityType === 'BATCH'
        ? ' This removes the Storage_Batch and retires its stored cards. Card records and images remain recoverable.'
        : ` This ${entityName} is empty; deleting it removes only the container record.`;
    if (!window.confirm(`Delete this ${entityName}${countText}?${detail} This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    const res = await api.deleteContainer(type, idBody(entity.entityId));
    setDeleting(false);
    if (!res.ok) {
      setError(res.message ?? res.error);
      return;
    }
    navigate(`/storage?section=${encodeURIComponent(sectionForType(type))}`, { replace: true });
  };

  return (
    <div>
      {/* Header row: heading + id + plain detail lines on the left (no "Details"
          box, matching the card detail page), the QR label tile pinned to the
          top-right of the page. The tile shows Save PDF + Print to its right and
          is hidden until the label loads. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 240, flex: '1 1 auto' }}>
          <p style={{ margin: '0 0 8px' }}>
            <Link
              className="tcvs-linklike"
              to={`/storage?section=${encodeURIComponent(backSection)}`}
              state={batchReturnState}
            >
              Back to {backSection}
            </Link>
          </p>
          <h2 style={{ marginTop: 0, marginBottom: 4 }}>{heading}</h2>
          <p
            className="tcvs-mono tcvs-muted"
            style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span>{entity.entityId}</span>
            <CopyIdButton value={entity.entityId} label={`Copy ${TYPE_LABEL[type]} ID`} />
          </p>
          {entity.entityType === 'SHELF' && 'shelfCode' in entity ? (
            <p style={{ margin: '2px 0' }}>Shelf code: {entity.shelfCode}</p>
          ) : null}
          {entity.entityType === 'RACK' && 'rackCode' in entity ? (
            <p style={{ margin: '2px 0' }}>Rack code: {entity.rackCode}</p>
          ) : null}
          {entity.entityType === 'ROW' && 'rowCode' in entity && entity.rowCode !== undefined ? (
            <p style={{ margin: '2px 0' }}>Row label: {entity.rowCode}</p>
          ) : null}
          {entity.entityType === 'BATCH' && 'sourceRunId' in entity && entity.sourceRunId ? (
            <p style={{ margin: '2px 0' }}>
              Source run: <code className="tcvs-mono">{entity.sourceRunId}</code>
              {'runState' in entity && entity.runState ? ` (${entity.runState})` : ''}
            </p>
          ) : null}
          <p style={{ margin: '2px 0' }}>Created: {shortDateTimeWithYear(entity.createdAt)}</p>
          {occupancy.tracked ? (
            <p style={{ margin: '2px 0' }}>
              Occupancy: {occupancy.childCount}
              {occupancy.capacity !== undefined ? ` / ${occupancy.capacity}` : ''}
              {occupancy.remainingCapacity !== undefined
                ? ` (${occupancy.remainingCapacity} free)`
                : ''}
            </p>
          ) : null}
          {entity.entityType === 'BATCH' ? (
            <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
              <p style={{ margin: 0 }}>
                Review: <ReviewStatusBadge status={detail.batchStatus} />{' '}
                <Link className="tcvs-linklike" to={batchReviewHref(entity.entityId)}>
                  Review batch
                </Link>
              </p>
              <p style={{ margin: 0 }}>
                Putaway: <PutawayStatusBadge status={detail.batchStatus} />
                {detail.batchStatus !== undefined && !detail.batchStatus.putAway ? (
                  <>
                    {' '}
                    <Link
                      className="tcvs-linklike"
                      to={`/storage?section=${encodeURIComponent('Putaway')}`}
                    >
                      Putaway Now
                    </Link>
                  </>
                ) : null}
              </p>
            </div>
          ) : null}
        </div>
        <div
          style={{
            display: 'grid',
            justifyItems: 'end',
            alignContent: 'start',
            gap: 12,
            flex: '0 1 360px',
          }}
        >
          <label className="tcvs-field" style={{ width: '100%', maxWidth: 360 }}>
            <span>Find/Filter</span>
            <input
              type="search"
              value={contentsQuery}
              onChange={(e) => setContentsQuery(e.target.value)}
              placeholder="SKU, short ID, card ID, container ID..."
              aria-label="Find or filter contents"
              className="tcvs-mono"
              style={{ width: '100%' }}
            />
          </label>
          {label !== null ? (
            <LabelTile
              token={label.token}
              entityId={'entityId' in label ? label.entityId : (id ?? '')}
              /* On-screen QR at ~1/4 the previous displayed size. The printed label
                 is unaffected — `@media print` forces physical label sizing. */
              qrMaxWidth={65}
              containerStyle={{
                border: '2px dashed #333',
                padding: 12,
                display: 'inline-grid',
                textAlign: 'center',
              }}
            >
              <button type="button" onClick={printSingleLabel}>
                Print
              </button>
            </LabelTile>
          ) : null}
        </div>
      </div>

      <LocationBlock location={location} selfId={entity.entityId} />

      {entity.entityType === 'BATCH' ? (
        <>
          <BatchTransferTools batchId={entity.entityId} onImported={refresh} />
          <BatchChecklistControl batchId={entity.entityId} onApplied={refresh} />
        </>
      ) : null}

      <ContentsPreview type={type} entityId={entity.entityId} query={contentsQuery} />

      <TransactionsPreview type={type} id={id ?? ''} />

      {(entity.entityType === 'BATCH' && isAdmin) ||
      (entity.entityType !== 'BATCH' && (canDeleteEmptyContainer || deletion.allowed === false)) ? (
        <div style={{ marginTop: 8, marginBottom: 16 }}>
          {entity.entityType === 'BATCH' || canDeleteEmptyContainer ? (
            <button
              type="button"
              className="tcvs-btn-danger tcvs-btn-compact"
              disabled={deleting}
              onClick={() => void onDeleteContainer()}
            >
              {deleteLabel}
            </button>
          ) : (
            <p className="tcvs-muted" style={{ margin: 0, fontSize: 12 }}>
              Delete is available once this container is empty.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function sectionForType(type: ContainerType): 'Racks' | 'Shelves' | 'Boxes' | 'Rows' | 'Batches' {
  switch (type) {
    case 'racks':
      return 'Racks';
    case 'shelves':
      return 'Shelves';
    case 'boxes':
      return 'Boxes';
    case 'rows':
      return 'Rows';
    case 'batches':
      return 'Batches';
  }
}

/**
 * Render the entity's resolved location as a linked parent chain. A Box links to
 * its Shelf; a Storage_Batch links to its Box and Shelf — so the detail page is
 * navigable up the hierarchy. A Shelf has no parent.
 */
function LocationBlock({
  location,
  selfId,
}: {
  location: StorageEntityDetailResponse['location'];
  selfId: string;
}): JSX.Element {
  const partialPath = location.status === 'UNPLACED' ? location.path : undefined;

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Location</h3>
      {location.status === 'PLACED' ? (
        <p style={{ margin: 0 }}>
          <LocationPathCrumbs path={location.path} selfId={selfId} />
        </p>
      ) : partialPath !== undefined ? (
        <>
          <p className="tcvs-muted" style={{ margin: '0 0 4px' }}>
            <strong className="tcvs-tone-unplaced">Not fully stored</strong>
          </p>
          <p style={{ margin: 0 }}>
            <LocationPathCrumbs path={partialPath} selfId={selfId} />
          </p>
        </>
      ) : (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          Not yet placed.
        </p>
      )}
    </section>
  );
}

type DetailLocationPath = Extract<
  StorageEntityDetailResponse['location'],
  { status: 'PLACED' }
>['path'];

function LocationPathCrumbs({
  path,
  selfId,
}: {
  path: DetailLocationPath;
  selfId: string;
}): JSX.Element {
  const parts: JSX.Element[] = [];
  if (path.rack !== undefined) {
    parts.push(
      <EntityCrumb
        type="racks"
        entityId={path.rack.entityId}
        label={`Rack ${path.rack.rackCode}`}
        selfId={selfId}
      />,
    );
  }
  if (path.shelf !== undefined) {
    parts.push(
      <EntityCrumb
        type="shelves"
        entityId={path.shelf.entityId}
        label={`Shelf ${path.shelf.shelfCode}`}
        selfId={selfId}
      />,
    );
  }
  if (path.box !== undefined) {
    parts.push(
      <EntityCrumb
        type="boxes"
        entityId={path.box.entityId}
        label={`Box ${idBody(path.box.entityId)}`}
        selfId={selfId}
      />,
    );
  }
  if (path.row !== undefined) {
    parts.push(
      <EntityCrumb
        type="rows"
        entityId={path.row.entityId}
        label={`Row ${path.row.rowCode ?? idBody(path.row.entityId)}`}
        selfId={selfId}
      />,
    );
  }
  if (path.batch !== undefined) {
    parts.push(
      <EntityCrumb
        type="batches"
        entityId={path.batch.entityId}
        label={`Batch ${idBody(path.batch.entityId)}`}
        selfId={selfId}
      />,
    );
  }
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? ' -> ' : null}
          {part}
        </Fragment>
      ))}
    </>
  );
}

function sortDrillContainersByCreated(
  children: readonly DrillChild[],
  direction: SortDirection,
): DrillChild[] {
  const factor = direction === 'asc' ? 1 : -1;
  return children
    .map((child, index) => ({ child, index }))
    .sort((a, b) => {
      if (a.child.kind !== 'container' || b.child.kind !== 'container') return a.index - b.index;
      const at = entityCreatedAt(a.child.entityId)?.getTime();
      const bt = entityCreatedAt(b.child.entityId)?.getTime();
      if (at === undefined && bt === undefined) return a.index - b.index;
      if (at === undefined) return 1;
      if (bt === undefined) return -1;
      if (at !== bt) return (at - bt) * factor;
      return a.index - b.index;
    })
    .map(({ child }) => child);
}

/** One node in the linked location chain; the entity itself isn't a link. */
function EntityCrumb({
  type,
  entityId,
  label,
  selfId,
}: {
  type: ContainerType;
  entityId: string;
  label: string;
  selfId: string;
}): JSX.Element {
  if (entityId === selfId) return <strong>{label}</strong>;
  return <Link to={`/storage/${type}/${encodeURIComponent(idBody(entityId))}`}>{label}</Link>;
}

/**
 * List the entity's immediate contents as clickable links so the operator can
 * drill down: a Shelf lists its Boxes, a Box lists its Storage_Batches, a Batch
 * lists its STORED Cards. Container children link to their own detail page
 * (`/storage/{type}/{id}`); card children link to the catalog detail page. Items
 * are paginated via the contents route's continuation token (Load more).
 */
function ContentsPreview({
  type,
  entityId,
  query,
}: {
  type: ContainerType;
  entityId: string;
  query: string;
}): JSX.Element {
  const api = useStorageApi();
  const [children, setChildren] = useState<DrillChild[] | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [batchSortDir, setBatchSortDir] = useState<SortDirection>('desc');

  const load = useCallback(
    async (page: string | undefined) => {
      setLoading(true);
      const r = await api.contents(type, idBody(entityId), page);
      setLoading(false);
      if (!r.ok) return;
      const mapped = r.data.items.map((item) => toDrillChild(r.data.kind, item));
      setChildren((prev) => (page === undefined ? mapped : [...(prev ?? []), ...mapped]));
      setNextToken(r.data.nextPageToken);
    },
    [api, type, entityId],
  );

  useEffect(() => {
    setChildren(null);
    setNextToken(undefined);
    void load(undefined);
  }, [load]);

  // What the children are called, for the empty/heading copy.
  const childNoun =
    type === 'racks'
      ? 'shelves'
      : type === 'shelves'
        ? 'boxes'
        : type === 'boxes'
          ? 'rows'
          : type === 'rows'
            ? 'batches'
            : 'cards';

  // Client-side filter over the loaded children (pure helper). Filtering only
  // sees what's loaded, so with more pages remaining we hint that below.
  const filtered = (children ?? []).filter((c) => drillChildMatches(c, query));
  const visible = type === 'rows' ? sortDrillContainersByCreated(filtered, batchSortDir) : filtered;
  const ownershipCardIds = visible.flatMap((child) =>
    child.kind === 'card' ? [child.cardId] : [],
  );
  const ownership = useOwnershipLabels(
    (children ?? []).flatMap((child) => (child.kind === 'card' ? [child.cardId] : [])),
  );
  const filtering = query.trim() !== '';

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Contents</h3>
      <OwnershipSummary cardIds={ownershipCardIds} labels={ownership} />
      {children !== null && children.length > 0 && type === 'rows' ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 8,
          }}
        >
          <button
            type="button"
            className="tcvs-btn-compact"
            onClick={() => setBatchSortDir((dir) => (dir === 'desc' ? 'asc' : 'desc'))}
            aria-label={`Sort batches by created date ${
              batchSortDir === 'desc' ? 'oldest first' : 'newest first'
            }`}
          >
            Created {batchSortDir === 'desc' ? '▼' : '▲'}
          </button>
        </div>
      ) : null}
      {children === null ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          Loading…
        </p>
      ) : children.length === 0 ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          No {childNoun} inside.
        </p>
      ) : visible.length === 0 ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          No {childNoun} match “{query.trim()}”.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {visible.map((child, i) => (
            <li
              key={i}
              style={{
                padding: '4px 0',
                borderTop: i > 0 ? '1px solid var(--tcvs-border)' : undefined,
              }}
            >
              {child.kind === 'container' ? (
                <Link to={entityDetailHref(child.entityType, child.entityId)}>
                  {ENTITY_TYPE_LABEL[child.entityType]}{' '}
                  <span className="tcvs-mono">{child.entityId}</span>
                  {child.entityType === 'BATCH' ? (
                    <span className="tcvs-muted">
                      {' '}
                      · Created {formatListingDate(child.entityId)}
                    </span>
                  ) : null}
                </Link>
              ) : child.kind === 'card' ? (
                <span
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 8,
                    textDecoration: 'none',
                  }}
                >
                  <CardThumbnailPair
                    cardId={child.cardId}
                    frontUrl={child.thumbnailUrl}
                    backUrl={child.backThumbnailUrl}
                  />
                  <Link to={`/catalog/${child.cardId}`} style={{ textDecoration: 'none' }}>
                    <span className="tcvs-mono">{child.shortCode ?? child.cardId}</span>
                    {child.sku !== undefined && child.sku !== '' ? (
                      <span className="tcvs-muted"> · SKU {child.sku}</span>
                    ) : null}
                    {child.currentBatchPosition !== undefined &&
                    child.currentBatchSize !== undefined ? (
                      <span className="tcvs-muted">
                        {' '}
                        · pick {child.currentBatchPosition} of {child.currentBatchSize}
                      </span>
                    ) : null}
                    {child.state !== undefined ? (
                      <span className="tcvs-muted"> ({child.state})</span>
                    ) : null}
                  </Link>
                  <OwnershipLabel label={ownership[child.cardId]} />
                </span>
              ) : (
                <span className="tcvs-mono tcvs-muted">{child.text}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {nextToken !== undefined ? (
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => void load(nextToken)} disabled={loading}>
            Load more
          </button>
          {filtering ? (
            <span className="tcvs-muted" style={{ marginLeft: 8, fontSize: 12 }}>
              Filter applies to loaded {childNoun} only — load more to search the rest.
            </span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function CardThumbnailPair({
  cardId,
  frontUrl,
  backUrl,
}: {
  cardId: string;
  frontUrl?: string;
  backUrl?: string;
}): JSX.Element {
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const [previewPosition, setPreviewPosition] = useState<SideHoverPreviewPosition | null>(null);
  const images = [
    frontUrl === undefined ? undefined : { url: frontUrl, label: 'Front image' },
    backUrl === undefined ? undefined : { url: backUrl, label: 'Back image' },
  ].filter((image): image is { url: string; label: string } => image !== undefined);

  const updatePreviewPosition = useCallback(() => {
    if (images.length === 0 || triggerRef.current === null) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPreviewPosition(
      positionSideHoverPreview(
        { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom },
        { width: window.innerWidth, height: window.innerHeight },
        contentsCardPreviewSize(images.length),
      ),
    );
  }, [images.length]);

  useEffect(() => {
    if (previewPosition === null) return;
    window.addEventListener('resize', updatePreviewPosition);
    window.addEventListener('scroll', updatePreviewPosition, true);
    return () => {
      window.removeEventListener('resize', updatePreviewPosition);
      window.removeEventListener('scroll', updatePreviewPosition, true);
    };
  }, [previewPosition, updatePreviewPosition]);

  return (
    <>
      <Link
        ref={triggerRef}
        to={`/catalog/${cardId}`}
        className="tcvs-card-thumbnail-pair-trigger"
        aria-label={`Open card ${cardId}; hover to preview images`}
        onMouseEnter={updatePreviewPosition}
        onMouseLeave={() => setPreviewPosition(null)}
        onFocus={updatePreviewPosition}
        onBlur={() => setPreviewPosition(null)}
      >
        <CardThumbnailImage url={frontUrl} label="Front image" />
        {backUrl !== undefined ? <CardThumbnailImage url={backUrl} label="Back image" /> : null}
      </Link>
      {previewPosition !== null && images.length > 0
        ? createPortal(
            <span
              className="tcvs-card-thumbnail-hover-portal"
              style={{ left: previewPosition.left, top: previewPosition.top }}
              aria-hidden="true"
            >
              {images.map((image) => (
                <img key={image.label} src={image.url} alt="" title={image.label} />
              ))}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

function CardThumbnailImage({ url, label }: { url?: string; label: string }): JSX.Element {
  const size = { width: 56, height: 78 } as const;
  if (url !== undefined) {
    return (
      <img
        src={url}
        alt={label}
        loading="lazy"
        width={size.width}
        height={size.height}
        style={{
          objectFit: 'cover',
          borderRadius: 3,
          border: '1px solid var(--tcvs-border)',
          flex: '0 0 auto',
        }}
      />
    );
  }
  return (
    <span
      aria-label={label}
      style={{
        ...size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 3,
        border: '1px solid var(--tcvs-border)',
        background: 'var(--tcvs-th-bg)',
        fontSize: 28,
        flex: '0 0 auto',
      }}
    >
      🂠
    </span>
  );
}

/** First page of this entity's transactions (audit trail), newest first. */
function TransactionsPreview({ type, id }: { type: ContainerType; id: string }): JSX.Element {
  const api = useStorageApi();
  const [res, setRes] = useState<TransactionsResponse | null>(null);

  useEffect(() => {
    let live = true;
    void api.transactions(type, id).then((r) => {
      if (live && r.ok) setRes(r.data);
    });
    return () => {
      live = false;
    };
  }, [api, type, id]);

  if (res === null)
    return (
      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>History</h3>
        <p className="tcvs-muted" style={{ margin: 0 }}>
          Loading…
        </p>
      </section>
    );

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>History</h3>
      {res.items.length === 0 ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          No recorded transactions.
        </p>
      ) : (
        <ul className="tcvs-mono" style={{ margin: 0, fontSize: 13 }}>
          {res.items.map((t) => (
            <li key={t.txnId}>
              {t.timestamp} — {t.opType}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
